import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onboard } from "../api";

/**
 * 6-step client onboarding wizard.
 *   1. Basic info
 *   2. Data sources (GA4 + GSC minimum)
 *   3. Brand keywords
 *   4. GEO queries (pulls industry templates, admin reviews / edits)
 *   5. Test connections
 *   6. Activate (initial-collect + route to client detail)
 */
export default function AgencyOnboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [clientId, setClientId] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    slug: "", name: "", website_url: "", industry: "marketing_agency",
    location: "", contact_name: "", contact_email: "",
    brand_keywords: "",
    ga4_property_id: "", gsc_property_url: "",
    geo_queries: [],
  });
  const [templates, setTemplates] = useState([]);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    onboard.geoTemplates().then(setTemplates).catch(() => {});
  }, []);

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function saveStep1() {
    setErr(null); setBusy(true);
    try {
      const client = await onboard.createClient({
        slug: form.slug,
        name: form.name,
        website_url: form.website_url,
        industry: form.industry,
        location: form.location,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        brand_keywords: form.brand_keywords
          .split(",").map((s) => s.trim()).filter(Boolean),
        onboarding_status: "pending",
        settings: {},
      });
      setClientId(client.id);
      setStep(2);
    } catch (x) {
      setErr(x?.response?.data?.detail || x.message);
    } finally { setBusy(false); }
  }

  async function saveStep2() {
    setErr(null); setBusy(true);
    try {
      if (form.ga4_property_id) {
        await onboard.addDataSource({
          client_id: clientId, source_type: "ga4",
          config: { property_id: form.ga4_property_id },
          credentials_ref: "google_service_account",
          is_active: true,
        });
      }
      if (form.gsc_property_url) {
        await onboard.addDataSource({
          client_id: clientId, source_type: "gsc",
          config: { property_url: form.gsc_property_url },
          credentials_ref: "google_service_account",
          is_active: true,
        });
      }
      // Always attach a GEO source with defaults — the admin tunes queries in step 4.
      await onboard.addDataSource({
        client_id: clientId, source_type: "geo",
        config: { platforms: ["chatgpt", "gemini"] },
        credentials_ref: "geo_api_keys",
        is_active: true,
      });
      setStep(3);
    } catch (x) {
      setErr(x?.response?.data?.detail || x.message);
    } finally { setBusy(false); }
  }

  function step3Next() {
    // Load GEO template for the selected industry into editable state.
    const tpl = templates.find((t) => t.industry === form.industry);
    if (tpl && form.geo_queries.length === 0) {
      up("geo_queries", tpl.queries.map((q) => ({ ...q })));
    }
    setStep(4);
  }

  async function saveStep4() {
    setErr(null); setBusy(true);
    try {
      await onboard.setGeoQueries(clientId, form.geo_queries);
      setStep(5);
    } catch (x) {
      setErr(x?.response?.data?.detail || x.message);
    } finally { setBusy(false); }
  }

  async function runTest(sourceType) {
    try {
      const r = await onboard.testConnection(clientId, sourceType);
      setTestResults((s) => ({ ...s, [sourceType]: r }));
    } catch (x) {
      setTestResults((s) => ({ ...s, [sourceType]: { ok: false, message: x.message } }));
    }
  }

  async function activate() {
    setErr(null); setBusy(true);
    try {
      await onboard.initialCollect(clientId);
      nav(`/clients/${clientId}`);
    } catch (x) {
      setErr(x?.response?.data?.detail || x.message);
    } finally { setBusy(false); }
  }

  const stepLabels = ["Basics", "Data sources", "Keywords", "GEO queries", "Test", "Activate"];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Onboard a client</h1>
          <div className="subtle">6 quick steps — approx 10 minutes.</div>
        </div>
      </div>

      <div className="wizard-steps">
        {stepLabels.map((lbl, i) => (
          <div key={i} className={`step ${step === i + 1 ? "active" : step > i + 1 ? "done" : ""}`}>
            {i + 1}. {lbl}
          </div>
        ))}
      </div>

      {err && <div className="error-box">{err}</div>}

      <div className="card">
        {step === 1 && <Step1 form={form} up={up} onNext={saveStep1} busy={busy} />}
        {step === 2 && <Step2 form={form} up={up} onBack={() => setStep(1)} onNext={saveStep2} busy={busy} />}
        {step === 3 && <Step3 form={form} up={up} onBack={() => setStep(2)} onNext={step3Next} />}
        {step === 4 && <Step4 form={form} up={up} templates={templates} onBack={() => setStep(3)} onNext={saveStep4} busy={busy} />}
        {step === 5 && <Step5 onTest={runTest} results={testResults} onBack={() => setStep(4)} onNext={() => setStep(6)} />}
        {step === 6 && <Step6 onBack={() => setStep(5)} onActivate={activate} busy={busy} />}
      </div>
    </div>
  );
}

// --------------------------- Step components ---------------------------

function Step1({ form, up, onNext, busy }) {
  return (
    <>
      <h2>Client basics</h2>
      <div className="grid grid-2">
        <div><label>Name</label><input value={form.name} onChange={(e) => up("name", e.target.value)} /></div>
        <div><label>Slug (url-safe)</label><input value={form.slug} onChange={(e) => up("slug", e.target.value)} placeholder="cycp" /></div>
        <div><label>Website URL</label><input value={form.website_url} onChange={(e) => up("website_url", e.target.value)} placeholder="https://example.com" /></div>
        <div>
          <label>Industry</label>
          <select value={form.industry} onChange={(e) => up("industry", e.target.value)}>
            <option value="marketing_agency">Marketing agency</option>
            <option value="nonprofit">Nonprofit</option>
            <option value="childcare">Childcare / franchise</option>
            <option value="local_business">Local business</option>
            <option value="professional_services">Professional services</option>
          </select>
        </div>
        <div><label>Location</label><input value={form.location} onChange={(e) => up("location", e.target.value)} placeholder="Atlanta, GA" /></div>
        <div><label>Contact name</label><input value={form.contact_name} onChange={(e) => up("contact_name", e.target.value)} /></div>
        <div><label>Contact email</label><input value={form.contact_email} onChange={(e) => up("contact_email", e.target.value)} /></div>
      </div>
      <div className="wizard-actions">
        <span />
        <button disabled={busy || !form.name || !form.slug} onClick={onNext}>
          {busy ? "Saving…" : "Continue"}
        </button>
      </div>
    </>
  );
}

function Step2({ form, up, onBack, onNext, busy }) {
  return (
    <>
      <h2>Data sources</h2>
      <p className="subtle">Minimum: GA4 + GSC. The rest can be added later from the client's detail page.</p>
      <div className="grid grid-2">
        <div>
          <label>GA4 property id</label>
          <input value={form.ga4_property_id} onChange={(e) => up("ga4_property_id", e.target.value)} placeholder="properties/123456789" />
        </div>
        <div>
          <label>GSC property URL</label>
          <input value={form.gsc_property_url} onChange={(e) => up("gsc_property_url", e.target.value)} placeholder="https://example.com/" />
        </div>
      </div>
      <p className="subtle" style={{ marginTop: 12 }}>
        Credentials are managed separately (service account / OAuth); the admin UI only wires up the config keys here.
      </p>
      <div className="wizard-actions">
        <button className="secondary" onClick={onBack} disabled={busy}>Back</button>
        <button onClick={onNext} disabled={busy}>{busy ? "Saving…" : "Continue"}</button>
      </div>
    </>
  );
}

function Step3({ form, up, onBack, onNext }) {
  return (
    <>
      <h2>Brand keywords</h2>
      <p className="subtle">Comma-separated. These are the brand terms we'll track in GSC and in the GEO brand queries.</p>
      <label>Keywords</label>
      <textarea
        rows={3}
        value={form.brand_keywords}
        onChange={(e) => up("brand_keywords", e.target.value)}
        placeholder="company name, brand nickname, domain"
      />
      <div className="wizard-actions">
        <button className="secondary" onClick={onBack}>Back</button>
        <button onClick={onNext}>Continue</button>
      </div>
    </>
  );
}

function Step4({ form, up, templates, onBack, onNext, busy }) {
  function updateQuery(i, patch) {
    const next = [...form.geo_queries];
    next[i] = { ...next[i], ...patch };
    up("geo_queries", next);
  }
  function addQuery() {
    const maxId = form.geo_queries.reduce((m, q) => Math.max(m, q.id || 0), 0);
    up("geo_queries", [...form.geo_queries, { id: maxId + 1, category: "brand", text: "" }]);
  }
  function removeQuery(i) {
    up("geo_queries", form.geo_queries.filter((_, idx) => idx !== i));
  }
  function loadTemplate(industry) {
    const tpl = templates.find((t) => t.industry === industry);
    if (tpl) up("geo_queries", tpl.queries.map((q) => ({ ...q })));
  }

  return (
    <>
      <h2>GEO queries</h2>
      <p className="subtle">
        These are the queries we'll ask ChatGPT, Gemini, etc. Edit to include the client's actual city, service, and name.
        Templates use placeholders like <code>{"{company_name}"}</code>, <code>{"{city}"}</code>, <code>{"{service}"}</code>.
      </p>

      <div style={{ marginBottom: 12 }}>
        <label>Load template</label>
        <select onChange={(e) => loadTemplate(e.target.value)} defaultValue="">
          <option value="" disabled>— pick industry —</option>
          {templates.map((t) => (
            <option key={t.industry} value={t.industry}>{t.industry}</option>
          ))}
        </select>
      </div>

      {form.geo_queries.length === 0 ? (
        <div className="empty">No queries yet. Load a template to start.</div>
      ) : (
        <table className="data">
          <thead>
            <tr><th>#</th><th>Category</th><th>Query</th><th></th></tr>
          </thead>
          <tbody>
            {form.geo_queries.map((q, i) => (
              <tr key={i}>
                <td>{q.id}</td>
                <td>
                  <select value={q.category} onChange={(e) => updateQuery(i, { category: e.target.value })}>
                    {["brand", "local", "service", "industry", "educational",
                      "comparison", "credibility", "client_association"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </td>
                <td><input value={q.text} onChange={(e) => updateQuery(i, { text: e.target.value })} /></td>
                <td><button className="ghost" onClick={() => removeQuery(i)}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <button className="secondary" onClick={addQuery} style={{ marginTop: 10 }}>+ Add query</button>

      <div className="wizard-actions">
        <button className="secondary" onClick={onBack} disabled={busy}>Back</button>
        <button onClick={onNext} disabled={busy || form.geo_queries.length === 0}>{busy ? "Saving…" : "Continue"}</button>
      </div>
    </>
  );
}

function Step5({ onTest, results, onBack, onNext }) {
  const sources = ["ga4", "gsc", "geo"];
  return (
    <>
      <h2>Test connections</h2>
      <p className="subtle">We'll validate that each data source has the config it needs. Live API smoke-tests land in Stage 7.</p>
      <table className="data">
        <thead><tr><th>Source</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {sources.map((s) => {
            const r = results[s];
            return (
              <tr key={s}>
                <td>{s}</td>
                <td>
                  {!r ? <span className="subtle">— not tested</span> :
                    r.ok ? <span style={{ color: "var(--good)" }}>✓ {r.message}</span>
                         : <span style={{ color: "var(--bad)" }}>✗ {r.message}</span>}
                </td>
                <td><button className="secondary" onClick={() => onTest(s)}>Test</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="wizard-actions">
        <button className="secondary" onClick={onBack}>Back</button>
        <button onClick={onNext}>Continue</button>
      </div>
    </>
  );
}

function Step6({ onBack, onActivate, busy }) {
  return (
    <>
      <h2>Activate</h2>
      <p>
        Everything's wired up. "Activate" flips the client's status to <code>collecting</code>.
        The next scheduled cron run will pick up this client and start collecting data automatically
        (daily 06:00 UTC for GSC + GA4, weekly Sunday 02:00 UTC for PageSpeed + crawl).
      </p>
      <p className="subtle" style={{ fontSize: 13, marginTop: 8 }}>
        For historical data, run <code>scripts/backfill.py --client &lt;slug&gt; --days 180</code> on the
        server once activation completes.
      </p>
      <div className="wizard-actions">
        <button className="secondary" onClick={onBack} disabled={busy}>Back</button>
        <button onClick={onActivate} disabled={busy}>{busy ? "Activating…" : "Activate & finish"}</button>
      </div>
    </>
  );
}
